import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  inject
} from "@angular/core";
import { ColumnStore } from "@bryntum/grid-thin";
import {
  EventModel,
  Scheduler,
  SchedulerConfig
} from "@bryntum/scheduler-thin";

import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import { ResourceStore } from "@app/services/store/resource-store";
import moment from "moment";
import { DiaryDefaults } from "@app/view/diary/diary-viewer/diary-defaults";
import { SkillStore } from "@app/services/store/skill-store";
import { ZoneStore } from "@app/services/store/zone-store";
import { TeamStore } from "@app/services/store/team-store";
import { UserService } from "@app/services/rest/user.service";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { Router } from "@angular/router";
import { AvailabilityCodeStore } from "@app/services/store/availability-code-store";
import { AvailabilityCode } from "@app/models/rest/availability-code";
import { AvailabilityType } from "@app/models/enums/availability-type.enum";
import {
  ResourceAvailability,
  ResourceAvailability_DetailProjection
} from "@app/models/rest/resource-availability";
import { ScheduleLayout } from "@app/core/schedule/schedule-layout";
import {
  buildSkillText,
  buildZoneText,
  getEffectiveSkills,
  getEffectiveZones
} from "@app/view/planning/schedule-viewer/effective-resource-facts";
import { NgxUiLoaderModule, NgxUiLoaderService } from "ngx-ui-loader";
import { DiaryService } from "@app/view/diary/diary-service/diary.service";
import { IScheduleViewer } from "@app/core/schedule/schedule-viewer.interface";
import {
  AvailabilityEditorComponent,
  LiveUpdateData
} from "@app/view/diary/availability-editor/availability-editor.component";
import { MatDialog } from "@angular/material/dialog"; // eslint-disable-next-line max-len
import { AvailabilityDeleteConfirmDialogComponent } from "@app/view/diary/availability-delete-confirm-dialog/availability-delete-confirm-dialog.component";
import { CustomResourceModel } from "@app/models/bryntum-ext/custom-resource-model";
import { ResourceCollection } from "@lagoshny/ngx-hateoas-client";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { Collection, CollectionFilter } from "@bryntum/core-thin";

@Component({
  selector: "app-diary-viewer",
  template:
    "<div></div><ngx-ui-loader [loaderId]=\"'scheduleLoad'\" ></ngx-ui-loader>",
  imports: [NgxUiLoaderModule]
})
export class DiaryViewerComponent implements IScheduleViewer {
  protected element = inject(ElementRef);
  protected resourceStore = inject(ResourceStore);
  protected availabilityCodeStore = inject(AvailabilityCodeStore);
  protected userService = inject(UserService);
  protected resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  protected serviceErrorHandler = inject(ServiceErrorHandlerService);
  protected router = inject(Router);
  protected schedulerService = inject(DiaryService);
  protected ngxLoading = inject(NgxUiLoaderService);
  protected dialog = inject(MatDialog);
  config = inject(ClientConfigService);
  protected store = inject<Store<any>>(Store);
  protected skillStore = inject(SkillStore);
  protected zoneStore = inject(ZoneStore);
  protected teamStore = inject(TeamStore);

  // public loading: boolean = true;
  public error: boolean = false;
  public updatedTicketCount = 0;
  public startHour: number;
  public numberOfHours: number;
  @Output() schedulerEvents = new EventEmitter<object>();
  public initialized: boolean = false;
  @Output() public layoutChange = new EventEmitter<boolean>();
  private initStartDate: Date = null;
  private initEndDate: Date = null;
  // private scheduler: Scheduler;
  private codeList: AvailabilityCode[];
  private dataLoading: boolean = true;

  private canEdit: boolean = false;
  private resourcesLoaded: boolean = false;
  // Raw REST resources, kept so effective skills/zones can be recomputed
  // when the view date range changes (#1558).
  private loadedResources: any[] = [];

  // "Active only" filter: when true, hide resources that have no positive
  // (ASSIGN or null availabilityCode) shifts in the current time axis.
  private activeOnlyFilter: boolean = false;
  private static readonly ACTIVE_ONLY_FILTER_ID = "active-only-filter";

  constructor() {
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.canEdit =
        !!auth && auth.isAuthenticated && (auth.isAdmin || auth.isPlanner);
    });
  }

  // <editor-fold desc="Initialization">
  static createContextMenuItem(
    text: string,
    icon: string,
    handler: Function
  ): any {
    return {
      text: text,
      icon: icon,
      weight: 200,
      onItem: handler
    };
  }

  public applyScheduleLayout(scheduleLayout: ScheduleLayout) {
    // console.log("applying layout", scheduleLayout);
    this.setActiveOnly(scheduleLayout?.activeOnly === true);

    if (scheduleLayout?.columns === undefined) {
      return;
    }
    const scheduler: Scheduler = this.schedulerService.scheduler.getValue();

    const filters: any[] = (scheduler.resourceStore.filters as any).map(
      (filter) => {
        if (filter instanceof CollectionFilter) {
          return {
            value: filter.value,
            property: filter.property,
            operator: filter.operator
          };
        }
        return {};
      }
    );
    try {
      let changed = false;
      if (scheduleLayout.filters.length !== filters.length) {
        changed = true;
      } else {
        for (let i = 0; i < scheduleLayout.filters; i++) {
          if (scheduleLayout.filters[i].value !== filters[i].value) {
            changed = true;
          }
        }
      }
      if (changed) {
        console.log("Applying filters", scheduleLayout.filters);
        scheduler.resourceStore.clearFilters();
        scheduler.resourceStore.filter(scheduleLayout.filters);
      }
    } catch (e) {}

    console.log(scheduleLayout.sorters);
    if (scheduleLayout.sorters && scheduleLayout.sorters.length > 0) {
      scheduler.resourceStore
        .addSorter(scheduleLayout.sorters)
        .then(() => {})
        .catch((reason) => {
          console.log("Cannot add sorters: ", reason, scheduleLayout.sorters);
        });
    }
    scheduleLayout.columns.forEach((column) => {
      if (scheduler.columns instanceof ColumnStore) {
        const found = scheduler.columns.get(column.name);

        // console.log("found", found);
        if (found) {
          found.width = column.width;
          found.hidden = column.hidden;
        }
      }
    }, this);
  }

  // <editor-fold desc="Initialization">
  public getScheduleLayout(): ScheduleLayout {
    const layout = new ScheduleLayout();
    layout.activeOnly = this.activeOnlyFilter;
    return layout;
  }

  public setDate(
    date: moment.Moment,
    startHour: number,
    numberOfHours: number
  ) {
    // console.log(
    //   `Setting view to date=${date}, startHour=${startHour}, hourCount=${numberOfHours}`
    // );
    const newStart = date.startOf("day").add(startHour, "hours").local(true);
    const newEnd = moment(newStart).add(numberOfHours, "hours").local(true);
    this.initStartDate = newStart.parseZone().toDate();
    this.initEndDate = newEnd.parseZone().toDate();
    this.dataLoading = true;
    if (this.schedulerService.scheduler.getValue()) {
      this.schedulerService.scheduler
        .getValue()
        .setTimeSpan(this.initStartDate, this.initEndDate);
      // Skills/zones shown per resource depend on the view range (#1558).
      this.refreshEffectiveResourceFacts();
      // this.loading = true;
      this.ngxLoading.startBackground("scheduleLoad");
      this.loadAvailabilities();
    }
  }

  public performInit() {
    let me = this;
    if (this.initialized) {
      // console.log("skipping init as already initialised");
      return;
    }
    const config: Partial<SchedulerConfig> = {
      startDate: this.initStartDate,
      endDate: this.initEndDate,
      appendTo: this.element.nativeElement.firstElementChild,
      enableEventAnimations: !this.config.config.disableAnimation,
      useInitialAnimation: !this.config.config.disableAnimation,
      eventRenderer(display: { eventRecord: EventModel; renderData }) {
        return me.renderEvent(null, display);
      },
      allowOverlap: false,
      listeners: {
        beforeEventEditShow: this.beforeEventEditShow,
        beforeEventResizeFinalize: this.beforeEventResizeFinalise as any,
        // beforeEventResizeFinalize: this.beforeEventResizeFinalise,
        beforeEventAdd: this.beforeEventAdd,
        beforeEventSave: this.beforeEventSave,
        beforeEventDelete: this.beforeEventDelete,
        eventResizeEnd: this.eventResizeEnd,
        eventContextMenu: this.eventContextMenuHandler,
        // onBeforeSave: this.onBeforeSave,
        // onAfterSave: this.onAfterSave,
        // timeAxisChange: this.axisUpdate,
        cellClick: ({ record, column }) => {
          console.log("event");
          if (!record.id || typeof record.id !== "number") {
            return;
          }
          console.log(record);
          console.log(column);
          console.log("Navigating to resource:", record.id);
          this.router.navigate(["setup/resources", record.id]).then();
        },
        eventClick: ({ eventRecord, event }) => {
          console.log("event click", eventRecord, event);
          let dialogRef = this.dialog.open(AvailabilityEditorComponent, {
            panelClass: ["toolbar-popup-dialog", "availability-editor-dialog"],
            data: {
              resourceAvailabilityId: eventRecord.get("id"),
              liveUpdateCallback: (currentState: { data: LiveUpdateData }) => {
                this.liveUpdate(currentState.data, eventRecord);
              }
            }
          });
          dialogRef.afterClosed().subscribe((value) => {
            if (value && value.newRecord) {
              this.loadAvailability(value.newRecord);
            }
          });
        },
        catchAll(event) {
          this.schedulerEvents.emit(event);
        },
        timeAxisChange: () => {
          // Time span changed (zoom / pan / setTimeSpan). Reapply
          // the active-only resource filter against the new range.
          this.applyActiveOnlyFilter();
        },
        thisObj: this
      }
    };
    const features = {
      eventDragCreate: this.canEdit,
      eventResize: this.canEdit
    };

    this.schedulerService.updateScheduler(
      DiaryDefaults.Create(config, features)
    );
    this.schedulerService.scheduler.value.on(
      "beforedragcreatefinalize",
      ({ context, event }) => {
        console.log("beforedragcreatefinalize", event, context);
        context.async = true;
        let dialogRef = this.dialog.open(AvailabilityEditorComponent, {
          panelClass: "availability-editor-dialog",
          data: {
            // resourceAvailabilityId: eventRecord.id
            initialResourceId: context.eventContexts[0].resourceRecord.id,
            initialStart: context.startDate,
            initialEnd: context.endDate,
            liveUpdateCallback: (currentState: { data: LiveUpdateData }) => {
              this.liveUpdate(currentState.data, context.eventRecord);
            }
          }
        });
        dialogRef.afterClosed().subscribe((value) => {
          console.log("availability editor closed", value);
          if (!value || value.wasDeleted) {
            me.schedulerService.scheduler
              .getValue()
              .eventStore.remove(context.eventRecord.id);
            context.finalize();
          } else if (value.completed) {
            console.log("Was completed, removing ", context.eventRecord);
            console.log("Adding ", value.newRecord);
            me.schedulerService.scheduler
              .getValue()
              .eventStore.remove(context.eventRecord.id);
            context.finalize();
            let newRecord = value.newRecord;
            setTimeout(() => {
              console.log("Running add on", newRecord);
              me.loadAvailability(newRecord);
            }, 10);
          }
        });
      }
    );

    this.loadFilterData();
    this.subscribeToEntities();
    this.loadResources();

    this.ngxLoading.startBackground("scheduleLoad");
    this.error = false;

    this.availabilityCodeStore.loadAll().then((codeList) => {
      this.codeList = codeList.resources;
    });

    this.schedulerService.scheduler.getValue().componentRef = this;
    const columnStore: ColumnStore =
      this.schedulerService.scheduler.getValue().columns;
    columnStore.addListener({
      thisObj: this,
      change: (event) => {
        console.log("columns changed", event);
        this.layoutChange.emit(true);
      }
    });

    const resourceStore =
      this.schedulerService.scheduler.getValue().resourceStore;
    console.log("Adding listener to resource store", resourceStore);
    let lastFilters: Collection = null;
    const context = this;
    let lastSort = null;
    resourceStore.reapplyFilterOnUpdate = true;
    resourceStore.reapplyFilterOnAdd = true;

    resourceStore.on({
      sort(event) {
        console.log("sort changed", event);
        console.log(event.sorters);
        if (!event.sorters && lastSort) {
          me.layoutChange.emit(true);
          return;
        }
        if (!lastSort) {
          lastSort = event.sorters;
          me.layoutChange.emit(true);
          return;
        }
        if (lastSort.length !== event.sorters.length) {
          lastSort = event.sorters;
          me.layoutChange.emit(true);
          return;
        }
        if (!lastSort[0]) {
          return;
        }
        if (
          lastSort[0].field !== event.sorters[0].field ||
          lastSort[0].ascending !== event.sorters[0].ascending
        ) {
          lastSort = event.sorters;
          me.layoutChange.emit(true);
          return;
        }
        // me.layoutChange.emit(true);
      },
      filter(event) {
        console.log("filter changed", event);
        const currentFilters: Collection = event.filters;
        if (!lastFilters) {
          lastFilters = new Collection({});
          lastFilters.add(currentFilters.values);
          return;
        }
        var changed = false;
        (currentFilters as any).forEach((filter) => {
          var oldFilter = lastFilters.get(filter.id);
          // @ts-ignore
          if (!oldFilter || filter.value !== oldFilter.value) {
            changed = true;
          }
        });
        if (changed) {
          console.log("Changed");

          lastFilters.clear();
          lastFilters.add(currentFilters.values);
          context.layoutChange.emit(true);
        } else {
          console.log("Sames");
        }
      }
    });

    this.initialized = true;
    // }
  }

  renderEvent(scheduler: Scheduler, display: { eventRecord; renderData }) {
    // console.log("render", display);
    let codeDescription = "";
    let description = "&nbsp;";
    let entity = display.eventRecord.get("entity") as ResourceAvailability;

    if (entity) {
      description = entity.description ? entity.description : "";
    }
    if (entity && entity.availabilityCode) {
      codeDescription = entity.availabilityCode.description
        ? entity.availabilityCode.description
        : "";
      if (codeDescription === "null") {
        codeDescription = "";
      }
      switch (entity.availabilityCode.availabilityType) {
        case AvailabilityType.ASSIGN:
          display.renderData.eventColor = "green";
          break;
        case AvailabilityType.BLOCK:
          display.renderData.eventColor = "red";
          break;
        case AvailabilityType.EXTEND:
          display.renderData.eventColor = "orange";
          break;
      }
    }

    const startTime = moment(display.eventRecord.startDate).format("HH:mm");
    const endTime = moment(display.eventRecord.endDate).format("HH:mm");

    // Self-contained, inline-styled tile (mirrors the schedule view's renderer)
    // so the layout never depends on global CSS or absolute positioning: a flex
    // row that fills the bar, with the code/description ellipsised on the left
    // and the start/end times stacked on the right. (#1516)
    return `
<div style="position: relative; display: flex; flex: 1 1 auto; flex-direction: row; align-items: center; gap: 6px; height: 100%; width: 100%; padding: 2px 6px; box-sizing: border-box; overflow: hidden;">
  <div style="display: flex; flex-direction: column; justify-content: center; flex: 1 1 auto; min-width: 0; overflow: hidden;">
    <div style="font-weight: bold; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${codeDescription}</div>
    <div style="line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${description}</div>
  </div>
  <div style="display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; flex: 0 0 auto; font-size: 0.85em; line-height: 1.2;">
    <div>${startTime}</div>
    <div>${endTime}</div>
  </div>
</div>`;
  }

  handleResourceLoad(resources: ResourceCollection<any>) {
    // this.schedulerService.scheduler.getValue().resourceStore.removeAll();
    for (const resource of resources.resources) {
      let current = this.schedulerService.scheduler
        .getValue()
        .resourceStore.getById(resource.id);
      if (!current) {
        current = new CustomResourceModel({
          id: resource.id,
          name: resource.name
          // entity: resource,
          // team: 'No Team',
          // role: '',
          // skillText: ''
        });
        current.setResourceImageUrl(resource.imageUrl);
        // current.set("entity", resource);
        current.set("team", "No Team");
        current.set("workMode", resource.workMode);
        this.schedulerService.scheduler.getValue().resourceStore.add(current);
        this.schedulerService.scheduler.getValue().resourceStore.commit();
        current = this.schedulerService.scheduler
          .getValue()
          .resourceStore.getById(resource.id);
      }
      (current as CustomResourceModel).setResourceImageUrl(resource.imageUrl);
      current.set("name", resource.name);
      // current.set("entity", resource);

      current.set("workMode", resource.workMode);
      if (resource.team) {
        current.set("team", resource.team.name);
      }
      this.applyEffectiveFactTexts(current, resource);
    }
    this.loadedResources = resources.resources;
    this.resourcesLoaded = true;
    console.log("Resource load complete");
  }

  /**
   * Sets skillText/zoneText restricted to the assignments active for the
   * current view range (assignment start/end dates and mapping-pattern
   * windows — #1558). Both the columns and their filters read these fields.
   */
  private applyEffectiveFactTexts(record: any, resource: any) {
    record.set(
      "skillText",
      buildSkillText(
        getEffectiveSkills(
          resource.skillsList,
          this.initStartDate,
          this.initEndDate
        )
      )
    );
    record.set(
      "zoneText",
      buildZoneText(
        getEffectiveZones(
          resource.zonesList,
          this.initStartDate,
          this.initEndDate
        )
      )
    );
  }

  /** Recomputes effective skills/zones after the view date range changes. */
  private refreshEffectiveResourceFacts() {
    const scheduler = this.schedulerService.scheduler.getValue();
    if (!scheduler) {
      return;
    }
    const resourceStore: any = scheduler.resourceStore;
    this.loadedResources.forEach((resource) => {
      const record = resourceStore.getById(resource.id);
      if (record) {
        this.applyEffectiveFactTexts(record, resource);
      }
    });
    if (resourceStore.isFiltered) {
      resourceStore.filter();
    }
  }

  subscribeToEntities() {
    // console.warn("Subscribing to entities in diary viewer");

    this.resourceAvailabilityStore.entities.subscribe((availabilities) => {
      if (this.resourcesLoaded) {
        console.log("availability message", availabilities);
        availabilities.forEach(this.loadAvailability.bind(this));
        // Resources visibility may need to change when availabilities update.
        this.applyActiveOnlyFilter();
      }
    });
    this.resourceAvailabilityStore.deleted.subscribe((value) => {
      if (value) {
        this.schedulerService.scheduler.getValue().eventStore.remove(value);
        this.applyActiveOnlyFilter();
      }
    });
  }

  loadResources() {
    // ?plannerId=1
    this.userService.getPlanner().then((planner) => {
      this.resourceStore
        .loadWithSearch(
          "findAllByPlannerId",
          {
            params: {
              plannerId: planner.id,
              projection: "detail"
            },
            pageParams: {
              page: 0,
              size: 5000
            }
          },
          true
        )
        .then((resources) => {
          this.handleResourceLoad(resources);
          this.loadAvailabilities();
        })
        .catch((error) => {
          this.ngxLoading.stopBackground("scheduleLoad");
          this.error = true;
          console.log("resource loading error", error);
        });
    });
  }

  loadAvailabilities() {
    this.resourceAvailabilityStore
      .findByTimeRange(this.initStartDate, this.initEndDate)
      .then(() => {
        this.ngxLoading.stopBackground("scheduleLoad");
        // Reapply the "active only" filter once availabilities have loaded
        // for the current time span.
        this.applyActiveOnlyFilter();
      });
  }

  /**
   * Toggle the "active only" filter. When enabled, resources without any
   * positive availability (null code or ASSIGN) within the currently
   * displayed time axis are hidden from the resource store.
   */
  public setActiveOnly(activeOnly: boolean) {
    if (this.activeOnlyFilter === activeOnly) {
      return;
    }
    this.activeOnlyFilter = activeOnly;
    this.applyActiveOnlyFilter();
  }

  public isActiveOnly(): boolean {
    return this.activeOnlyFilter;
  }

  /**
   * Apply (or clear) the active-only resource filter based on current state.
   * Looks at events in the visible time axis, collects the set of resource
   * ids that have at least one positive (non-BLOCK) availability, then
   * filters the resourceStore accordingly.
   */
  private applyActiveOnlyFilter() {
    const scheduler = this.schedulerService.scheduler.getValue();
    if (!scheduler) {
      return;
    }
    const resourceStore = scheduler.resourceStore;
    if (!resourceStore) {
      return;
    }

    if (!this.activeOnlyFilter) {
      resourceStore.removeFilter(DiaryViewerComponent.ACTIVE_ONLY_FILTER_ID);
      return;
    }

    const { eventStore } = scheduler;
    const resourceIdsWithPositiveShifts = new Set<any>();

    // Use eventStore.records (filtered set) so other event filters compose.
    eventStore.records.forEach((event: any) => {
      // Only count events that intersect the currently visible time axis.
      const eventStart = event.startDate ?? event.get?.("startDate");
      const eventEnd = event.endDate ?? event.get?.("endDate");
      if (!this.isTimeSpanInCurrentView(eventStart, eventEnd)) {
        return;
      }
      const entity = event.get
        ? (event.get("entity") as ResourceAvailability)
        : (event.entity as ResourceAvailability);
      // Positive shift = no code (null) or ASSIGN. BLOCK / EXTEND = not active.
      const isPositive =
        !entity ||
        !entity.availabilityCode ||
        entity.availabilityCode.availabilityType === AvailabilityType.ASSIGN;
      if (!isPositive) {
        return;
      }
      const resourceId = event.resourceId ?? event.get?.("resourceId");
      if (resourceId !== undefined && resourceId !== null) {
        resourceIdsWithPositiveShifts.add(resourceId);
      }
    });

    resourceStore.filter({
      id: DiaryViewerComponent.ACTIVE_ONLY_FILTER_ID,
      filterBy: (resource: any) =>
        resourceIdsWithPositiveShifts.has(resource.id)
    });
  }

  private isTimeSpanInCurrentView(startDate: Date, endDate: Date): boolean {
    if (!startDate || !endDate || !this.initStartDate || !this.initEndDate) {
      return false;
    }
    return startDate < this.initEndDate && endDate > this.initStartDate;
  }

  eventContextMenuHandler(state: { source; eventRecord; event }) {
    console.log("building context menu");
    if (state.eventRecord.eventType === "Ticket") {
      state.source.features.eventContextMenu.items = {
        deleteEvent: false,
        unassignEvent: false
      };
    }
  }

  beforeEventEditShow({ eventEdit, eventRecord, resourceRecord }) {
    console.log("before event edit");
    console.log(eventEdit);
    console.log(eventRecord);
    let me = this;

    if (eventRecord.entity) {
      // resourceAvailabilityId: number, initialResourceId: number, initialStart: Date, initialEnd: Date
      console.log("editing", eventRecord.entity);
      let dialogRef = this.dialog.open(AvailabilityEditorComponent, {
        panelClass: "availability-editor-dialog",
        data: {
          resourceAvailabilityId: eventRecord.id,
          liveUpdateCallback: (currentState: { data: LiveUpdateData }) => {
            this.liveUpdate(currentState.data, eventRecord);
          }
        }
      });
      dialogRef.afterClosed().subscribe((value) => {
        console.log("edit dialog closed", value);
        if (value.wasDeleted) {
          console.log("record was deleted");
          me.schedulerService.scheduler
            .getValue()
            .eventStore.remove(eventRecord.id);
        } else {
          console.log("triggering reload for availability", eventRecord.entity);
          this.resourceAvailabilityStore
            .loadSingleProjection(
              ResourceAvailability_DetailProjection,
              eventRecord.entity.id,
              "detail"
            )
            .then((value1) => {
              this.loadAvailability(value1);
            })
            .catch(() => {
              console.log(
                "Unable to retrieve availability after edit edit operation"
              );
            });
        }
      });
    } else {
      console.log("adding", eventRecord, resourceRecord);
      let dialogRef = this.dialog.open(AvailabilityEditorComponent, {
        panelClass: "availability-editor-dialog",
        data: {
          // resourceAvailabilityId: eventRecord.id
          initialResourceId: resourceRecord.id,
          initialStart: eventRecord.startDate,
          initialEnd: eventRecord.endDate,
          liveUpdateCallback: (currentState: { data: LiveUpdateData }) => {
            this.liveUpdate(currentState.data, eventRecord);
          }
        }
      });
      dialogRef.afterClosed().subscribe((value) => {
        console.log("availability editor closed after add operation", value);
        if (value.wasDeleted) {
          me.schedulerService.scheduler
            .getValue()
            .eventStore.remove(eventRecord.id);
        } else if (value.completed) {
          this.resourceAvailabilityStore
            .loadSingleProjection(
              ResourceAvailability_DetailProjection,
              value.newRecord.id,
              "detail"
            )
            .then((value1) => {
              this.loadAvailability(value1);
            })
            .catch(() => {
              console.log(
                "Unable to retrieve availability after edit edit operation"
              );
            });
        }
      });
    }
  }

  beforeEventAdd(event) {
    console.log("before event add", event);
  }

  beforeEventDelete({ eventRecords, context }) {
    let me = this;
    context.async = true;
    let deleteRef = me.dialog.open(
      AvailabilityDeleteConfirmDialogComponent,
      {}
    );
    deleteRef.afterClosed().subscribe((dialogResponse) => {
      console.log("delete dialog response", dialogResponse);
      if (dialogResponse.deleted) {
        me.resourceAvailabilityStore
          .loadSingle(eventRecords[0].get("id"))
          .then((storedAvailability) => {
            me.resourceAvailabilityStore.delete(storedAvailability).subscribe(
              () => {
                me.schedulerService.scheduler
                  .getValue()
                  .eventStore.remove(storedAvailability.id);
              },
              (error1) => {
                this.serviceErrorHandler.handleError(error1);
              }
            );
          });
      }
    });

    return false;
  }

  beforeEventSave({ source, eventRecord, resourceRecords, values }) {
    console.log("before event save");
    console.log(eventRecord);
    console.log(values);
    console.log(source.eventEdit.description.value);

    eventRecord.description = source.eventEdit.description.value;

    if (eventRecord.entity) {
      let resourceAvailability: ResourceAvailability = eventRecord.entity;
      this.resourceAvailabilityStore
        .loadSingle(resourceAvailability.id)
        .then((resourceAv) => {
          resourceAv.start = values.startDate;
          resourceAv.end = values.endDate;
          resourceAv.description = source.eventEdit.description.value;
          console.log("selected id", source.eventEdit.availabilityCode);
          resourceAv.availabilityCode = this.codeList.filter(
            (value) => value.id === source.eventEdit.availabilityCode.value
          )[0];
          this.resourceAvailabilityStore.update(resourceAv).subscribe(
            (added: ResourceAvailability) => {
              console.log(
                "adding availability code to resource availability",
                resourceAv,
                added
              );
              this.resourceAvailabilityStore
                .loadSingleProjection(
                  ResourceAvailability_DetailProjection,
                  resourceAv.id,
                  "detail"
                )
                .then((value) => {
                  value
                    .bindRelation(
                      "availabilityCode",
                      resourceAv.availabilityCode
                    )
                    .subscribe(() => {
                      this.resourceAvailabilityStore
                        .loadSingleProjection(
                          ResourceAvailability_DetailProjection,
                          resourceAv.id,
                          "detail"
                        )
                        .then((value1) => {
                          this.loadAvailability(value1);
                        })
                        .catch(() => {
                          console.log(
                            "Unable to retrieve availability after event save operation"
                          );
                        });
                    });
                });
            },
            () => {}
          );
        });
    } else {
      console.warn("before event add");

      console.log(eventRecord);
      let newAvailability = new ResourceAvailability();
      newAvailability.start = eventRecord.startDate;
      newAvailability.end = eventRecord.endDate;
      newAvailability.description = eventRecord.description;
      this.resourceStore
        .loadSingle(resourceRecords[0].data.id)
        .then((resource) => {
          newAvailability.resource = resource;
          this.resourceAvailabilityStore.create(newAvailability).subscribe(
            (result) => {
              eventRecord.entity = result;
              console.log("success, deleting temp proxy");
              console.log(source.eventEdit);
              if (
                source.eventEdit.availabilityCode &&
                source.eventEdit.availabilityCode.value
              ) {
                newAvailability.availabilityCode = this.codeList.filter(
                  (value) =>
                    value.id === source.eventEdit.availabilityCode.value
                )[0];
              }
              newAvailability
                .bindRelation(
                  "availabilityCode",
                  newAvailability.availabilityCode
                )
                .subscribe((value) => {
                  console.log("relationship added", value, eventRecord);
                  eventRecord.set("description", newAvailability.description);
                });
            },
            (error1) => {
              console.log("error adding availability");
              this.serviceErrorHandler.handleError(error1);
              this.schedulerService.scheduler
                .getValue()
                .eventStore.remove([eventRecord]);
            }
          );
        });
      // console.log('attempting to remove event');
      // }
      // return false;
    }
  }

  beforeEventResizeFinalise(event) {
    this.resourceAvailabilityStore
      .loadSingle(event.context.eventRecord.id)
      .then((resourceAv) => {
        resourceAv.start = event.context.startDate;
        resourceAv.end = event.context.endDate;

        this.resourceAvailabilityStore.update(resourceAv).subscribe(
          () => {
            event.context.finalize();
          },
          (error) => {
            event.context.finalize(false);
            this.serviceErrorHandler.handleError(error);
            this.loadAvailabilities();
          }
        );
      })
      .catch((error) => {
        event.context.finalize(false);
        this.serviceErrorHandler.handleError(error);
      });

    return false;
  }

  eventResizeEnd() {}

  loadAvailability(availability: ResourceAvailability_DetailProjection) {
    let event = this.schedulerService.scheduler
      .getValue()
      .eventStore.getById(availability.id);
    if (!event) {
      event = new EventModel({
        id: availability.id,
        // eventType: 'Availability',
        eventColor:
          availability.availabilityCode &&
          availability.availabilityCode.availabilityType ===
            AvailabilityType.BLOCK
            ? "red"
            : "green",
        resizable: true,
        draggable: false,
        startDate: new Date(availability.start),
        endDate: new Date(availability.end),
        resourceId: availability.resource.id
      });
      event.set("entity", availability);
      event.set("description", availability.description);
      this.schedulerService.scheduler.getValue().eventStore.add([event]);
      this.schedulerService.scheduler.getValue().eventStore.commit();
      return;
    }

    // Update times and description
    if (event.get("startDate") !== availability.start) {
      event.set("startDate", new Date(availability.start));
    }
    if (event.get("endDate") !== availability.end) {
      event.set("endDate", new Date(availability.end));
    }
    event.set("description", availability.description);

    // Preserve existing availability code if incoming update doesn't have one
    const existingEntity = event.get("entity");
    const existingAvailabilityCode = existingEntity?.availabilityCode;

    if (!availability.availabilityCode && existingAvailabilityCode) {
      console.log(
        `Preserving existing availability code for ${availability.id} - incoming update has no code`
      );
      // Keep the existing availability code
      availability.availabilityCode = existingAvailabilityCode;
    }

    // Update the event color based on availability code (preserved or new)
    event.set(
      "eventColor",
      availability.availabilityCode &&
        availability.availabilityCode.availabilityType ===
          AvailabilityType.BLOCK
        ? "red"
        : "green"
    );

    event.set("entity", availability);
  }

  private liveUpdate(updateData: LiveUpdateData, eventRecord: EventModel) {
    console.log("Got live update callback!", updateData, eventRecord);
    eventRecord.setStartEndDate(updateData.newStart, updateData.newEnd);

    if (!eventRecord.get("entity")) {
      eventRecord.set("entity", {
        availabilityCode: null,
        description: updateData.newDescription
      });
    }
    eventRecord.get("entity").description = updateData.newDescription;

    eventRecord.set("description", updateData.newDescription);
    if (updateData.newAvailabilityCode && eventRecord.get("entity")) {
      eventRecord.get("entity").availabilityCode =
        updateData.newAvailabilityCode;

      eventRecord.set(
        "eventColor",
        updateData.newAvailabilityCode &&
          updateData.newAvailabilityCode.availabilityType ===
            AvailabilityType.BLOCK
          ? "red"
          : "green"
      );
    } else {
      eventRecord.set("eventColor", "green");
      eventRecord.get("entity").availabilityCode = null;
    }
    // eventRecord.entity.description = currentState.newDescription;
  }

  private async loadFilterData() {
    try {
      // Load skills, zones, teams, and resources in parallel
      const [skillsResult, zonesResult, teamsResult, resourcesResult] =
        await Promise.all([
          this.skillStore.loadAll(),
          this.zoneStore.loadAll(),
          this.teamStore.loadAll(),
          this.resourceStore.loadAll()
        ]);

      console.log("Diary - Skills loaded:", skillsResult);
      console.log("Diary - Zones loaded:", zonesResult);
      console.log("Diary - Teams loaded:", teamsResult);
      console.log("Diary - Resources loaded:", resourcesResult);

      // Set the filter data in DiaryDefaults before scheduler creation
      DiaryDefaults.setFilterData(
        skillsResult.resources || [],
        zonesResult.resources || [],
        teamsResult.resources || [],
        resourcesResult.resources || []
      );

      console.log("Diary filter data set in DiaryDefaults");
    } catch (error) {
      console.error("Error loading diary filter data:", error);
    }
  }
}
